/**
 * SUNTRONIX ONE — Preflight approvals audit log.
 *
 * Every soft-check clearance issued through the Preflight modal lives
 * here.  Approvers can see who cleared what, why, and when — and revoke
 * an unexpired approval if the paperwork gap has closed.
 */
import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ClipboardCheck, ShieldOff, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

export default function PreflightApprovals() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [revoke, setRevoke] = useState(null); // { row }
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/preflight/approvals",
        { params: { active_only: !showAll, limit: 200 } });
      setRows(data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }, [showAll]);
  useEffect(() => { load(); }, [load, showAll]);

  const submitRevoke = async () => {
    if (reason.trim().length < 5) {
      toast.error("Reason must be at least 5 characters"); return;
    }
    setSaving(true);
    try {
      await api.post("/preflight/approvals/revoke",
        { approval_id: revoke.row.id, reason: reason.trim() });
      toast.success("Approval revoked");
      setRevoke(null); setReason("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 max-w-[1400px]" data-testid="preflight-approvals-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Administration · Preflight</div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Preflight approvals</h1>
          <p className="text-[13px] text-slate-500 mt-1 max-w-2xl">
            Every soft-rule clearance issued from the Preflight block dialog.  Hard
            blocks never appear here — those must be fixed at the source.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[12px] text-slate-600 flex items-center gap-1.5">
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)}
              data-testid="approvals-show-all" />
            Include expired / revoked
          </label>
          <Button size="sm" variant="outline" onClick={load} data-testid="approvals-refresh">
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-[10px] uppercase text-slate-500">
              <th className="text-left px-3 py-2.5">When</th>
              <th className="text-left px-3 py-2.5">Rule</th>
              <th className="text-left px-3 py-2.5">Entity</th>
              <th className="text-left px-3 py-2.5">Approver</th>
              <th className="text-left px-3 py-2.5">Justification</th>
              <th className="text-left px-3 py-2.5">Status</th>
              <th className="text-right px-3 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {busy && rows.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center"><Loader2 className="h-4 w-4 mx-auto animate-spin" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400 text-[13px]">
                No preflight approvals recorded yet.
              </td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} data-testid={`approval-row-${r.id}`} className="hover:bg-slate-50">
                <td className="px-3 py-2 text-xs text-slate-600">
                  <div>{r.at ? formatDistanceToNow(new Date(r.at), { addSuffix: true }) : "—"}</div>
                  <div className="text-[10px] text-slate-400 font-mono">{r.at?.slice(0, 19).replace("T", " ")}</div>
                </td>
                <td className="px-3 py-2">
                  <div className="text-[13px] font-medium text-slate-900">{r.rule_name || r.rule_id}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{r.rule_id}</div>
                </td>
                <td className="px-3 py-2 text-xs">
                  <div className="text-slate-700 capitalize">{(r.entity_kind || "").replace(/_/g, " ")}</div>
                  <div className="text-[10px] text-slate-400 font-mono">{(r.entity_id || "").slice(0, 8)}…</div>
                </td>
                <td className="px-3 py-2 text-xs">
                  <div className="text-slate-900 font-mono">{r.approver_email}</div>
                  <div className="text-[10px] text-slate-400 capitalize">{(r.approver_role || "").replace(/_/g, " ")}</div>
                </td>
                <td className="px-3 py-2 text-[12px] text-slate-700 max-w-[260px]">
                  <div className="line-clamp-2" title={r.justification}>{r.justification}</div>
                </td>
                <td className="px-3 py-2">
                  <ApprovalBadge r={r} />
                </td>
                <td className="px-3 py-2 text-right">
                  {r.active && !r.is_expired && (
                    <Button size="sm" variant="ghost" className="h-7 text-rose-600"
                      onClick={() => setRevoke({ row: r })}
                      data-testid={`approval-revoke-${r.id}`}>
                      <ShieldOff className="h-3.5 w-3.5 mr-1" /> Revoke
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!revoke} onOpenChange={(v) => { if (!v) { setRevoke(null); setReason(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="revoke-modal">
          <DialogHeader>
            <DialogTitle>Revoke approval · {revoke?.row?.rule_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-[12px] text-slate-600">
              The clearance for this entity will end immediately.  A future preflight
              run will surface the check again until it&apos;s properly resolved.
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Reason for revoke</label>
              <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                data-testid="revoke-reason"
                placeholder="e.g. Contract now on file — approval no longer needed." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRevoke(null); setReason(""); }}
              data-testid="revoke-cancel">Cancel</Button>
            <Button onClick={submitRevoke} disabled={saving} className="bg-slate-900"
              data-testid="revoke-submit">
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
              Revoke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ApprovalBadge({ r }) {
  let tone, label;
  if (!r.active) { tone = "bg-rose-100 text-rose-800"; label = "Revoked"; }
  else if (r.is_expired) { tone = "bg-slate-200 text-slate-600"; label = "Expired"; }
  else { tone = "bg-emerald-100 text-emerald-800"; label = "Active"; }
  return (
    <div>
      <span className={`px-2 py-0.5 rounded-full text-[10px] ${tone}`}>{label}</span>
      {r.expires_at && r.active && !r.is_expired && (
        <div className="text-[10px] text-slate-400 mt-0.5">
          Expires {formatDistanceToNow(new Date(r.expires_at), { addSuffix: true })}
        </div>
      )}
    </div>
  );
}
